import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Form, Spinner } from 'react-bootstrap';
import diseaseService from '../../services/diseaseService';
import Icon from '../common/Icons';
import './DiagnosisDiseaseField.css';

const MIN_SEARCH_LEN = 2;
const DEBOUNCE_MS = 280;

/**
 * Sélection de un ou plusieurs codes maladie (référentiel) + précisions optionnelles.
 */
export default function DiagnosisDiseaseField({
  selectedDiseases = [],
  onDiseasesChange,
  notes,
  onNotesChange,
  disabled = false
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const debounceRef = useRef(null);

  const selectedIds = new Set((selectedDiseases || []).map((d) => d.id));

  const runSearch = useCallback(async (term) => {
    const q = term.trim();
    if (q.length < MIN_SEARCH_LEN) {
      setResults([]);
      setSearchError('');
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const res = await diseaseService.getDiseases({ search: q });
      if (res.success) {
        setResults(Array.isArray(res.data) ? res.data.slice(0, 10) : []);
      } else {
        setResults([]);
        setSearchError(res.message || 'Recherche impossible');
      }
    } catch (e) {
      setResults([]);
      setSearchError(e.message || 'Recherche impossible');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  const pickDisease = (item) => {
    if (selectedIds.has(item.id)) {
      setQuery('');
      setResults([]);
      return;
    }
    onDiseasesChange([
      ...(selectedDiseases || []),
      { id: item.id, code: item.code, name: item.name }
    ]);
    setQuery('');
    setResults([]);
    setSearchError('');
  };

  const removeDisease = (id) => {
    onDiseasesChange((selectedDiseases || []).filter((d) => d.id !== id));
  };

  const visibleResults = results.filter((item) => !selectedIds.has(item.id));
  const count = (selectedDiseases || []).length;

  return (
    <div className="diag-disease-field">
      <div className="diag-disease-field__head">
        <Form.Label className="diag-disease-field__label mb-0">
          Diagnostic codé <span className="text-danger">*</span>
        </Form.Label>
        {count > 0 ? (
          <span className="diag-disease-field__count">
            {count} code{count > 1 ? 's' : ''}
          </span>
        ) : null}
      </div>

      {count > 0 ? (
        <div className="diag-disease-field__pills" role="list" aria-label="Codes maladie sélectionnés">
          {(selectedDiseases || []).map((d) => (
            <span key={d.id} className="diag-disease-field__pill" role="listitem">
              <code className="diag-disease-field__pill-code">{d.code}</code>
              <span className="diag-disease-field__pill-name" title={d.name}>
                {d.name}
              </span>
              <button
                type="button"
                className="diag-disease-field__pill-remove"
                onClick={() => removeDisease(d.id)}
                disabled={disabled}
                aria-label={`Retirer ${d.code} — ${d.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="diag-disease-field__empty">Aucun code — recherchez ci-dessous.</p>
      )}

      <div className="diag-disease-field__search">
        <div className="diag-disease-field__search-wrap">
          <Icon name="search" size={15} className="diag-disease-field__search-icon" aria-hidden />
          <Form.Control
            size="sm"
            className="diag-disease-field__search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Code ou libellé (ex. I10, hypertension…)"
            disabled={disabled}
            autoComplete="off"
            aria-describedby="diag-disease-search-hint"
          />
          {searching ? (
            <Spinner animation="border" size="sm" className="diag-disease-field__search-spin" />
          ) : null}
        </div>
        <Form.Text id="diag-disease-search-hint" muted className="diag-disease-field__hint">
          {MIN_SEARCH_LEN} caractères minimum
        </Form.Text>
      </div>

      {searchError ? (
        <Alert variant="warning" className="py-2 small mt-2 mb-0">
          {searchError}
        </Alert>
      ) : null}

      {visibleResults.length > 0 ? (
        <div className="diag-disease-field__hits" role="listbox" aria-label="Résultats de recherche">
          {visibleResults.map((item) => (
            <button
              key={item.id}
              type="button"
              role="option"
              className="diag-disease-field__hit-btn"
              onClick={() => pickDisease(item)}
              disabled={disabled}
            >
              <code className="diag-disease-field__hit-code">{item.code}</code>
              <span className="diag-disease-field__hit-name">{item.name}</span>
              <span className="diag-disease-field__hit-add" aria-hidden>
                +
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {!searching && query.trim().length >= MIN_SEARCH_LEN && visibleResults.length === 0 && !searchError ? (
        <p className="diag-disease-field__no-hit">Aucun résultat pour cette recherche.</p>
      ) : null}

      <Form.Group className="diag-disease-field__notes mb-0">
        <Form.Label className="diag-disease-field__notes-label">Précisions (optionnel)</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Complément clinique…"
          disabled={disabled}
        />
      </Form.Group>
    </div>
  );
}
